import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { Package, ShoppingCart, Users, IndianRupee, Clock, LayoutDashboard, Settings, ImageDown, Loader2, RefreshCw, FileText } from 'lucide-react';

export const AdminDashboard = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState(null);

  useEffect(() => {
    if (authLoading) return; // wait until auth context has hydrated from /auth/me
    if (!user || user.role !== 'admin') {
      navigate('/login');
      return;
    }
    api.get('/admin/dashboard')
      .then(res => setStats(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
    api.get('/admin/sync-images/last').then(res => setLastSync(res.data)).catch(() => {});
  }, [user, authLoading, navigate]);

  const handleSeedImages = async () => {
    if (!window.confirm(
      'Seed authentic puja-themed images from Unsplash into permanent storage ' +
      'and assign them to every product & category? Safe to re-run.'
    )) return;
    setSeeding(true);
    try {
      const res = await api.post('/admin/seed-images', null, { timeout: 240000 });
      const { images_uploaded, products_updated, categories_updated } = res.data;
      toast.success(
        `Seeded ${images_uploaded} images. Updated ${products_updated} products & ${categories_updated} categories.`
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Seed failed. Check backend logs.');
    } finally {
      setSeeding(false);
    }
  };

  const handleSyncImages = async () => {
    setSyncing(true);
    try {
      const res = await api.post('/admin/sync-images', null, { timeout: 120000 });
      setLastSync(res.data);
      const { checked, orphaned } = res.data;
      if (orphaned === 0) {
        toast.success(`Verified ${checked} images — all healthy.`);
      } else {
        toast.warning(`Checked ${checked}. ${orphaned} orphan(s) need attention.`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Sync failed.');
    } finally {
      setSyncing(false);
    }
  };

  const [seedingDesc, setSeedingDesc] = useState(false);
  const handleSeedDescriptions = async () => {
    if (!window.confirm(
      'Overwrite all product descriptions with the curated SEO-friendly copy? Safe to re-run.'
    )) return;
    setSeedingDesc(true);
    try {
      const res = await api.post('/admin/seed-descriptions', null, { timeout: 60000 });
      toast.success(`Updated ${res.data.updated} product description${res.data.updated === 1 ? '' : 's'}.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed.');
    } finally {
      setSeedingDesc(false);
    }
  };

  if (authLoading) return null;
  if (!user || user.role !== 'admin') return null;

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-orange-800">Admin Dashboard</h1>
        <div className="flex gap-2">
          <Link to="/admin/products" className="px-4 py-2 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200">
            Products
          </Link>
          <Link to="/admin/orders" className="px-4 py-2 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200">
            Orders
          </Link>
          <Link to="/admin/categories" className="px-4 py-2 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200">
            Categories
          </Link>
          <Link to="/admin/abandoned-carts" data-testid="nav-abandoned-carts" className="px-4 py-2 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200">
            Abandoned Carts
          </Link>
          <Link to="/admin/reviews" data-testid="nav-reviews" className="px-4 py-2 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200">
            Reviews
          </Link>
          <Link to="/admin/campaigns" data-testid="nav-campaigns" className="px-4 py-2 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200">
            Campaigns
          </Link>
          <Button
            data-testid="seed-images-btn"
            onClick={handleSeedImages}
            disabled={seeding}
            className="bg-orange-600 hover:bg-orange-700 text-white"
          >
            {seeding ? <Loader2 className="animate-spin mr-2" size={16} /> : <ImageDown className="mr-2" size={16} />}
            {seeding ? 'Seeding…' : 'Seed Demo Images'}
          </Button>
          <Button
            data-testid="sync-images-btn"
            onClick={handleSyncImages}
            disabled={syncing}
            variant="outline"
            className="border-orange-600 text-orange-700 hover:bg-orange-50"
          >
            {syncing ? <Loader2 className="animate-spin mr-2" size={16} /> : <RefreshCw className="mr-2" size={16} />}
            {syncing ? 'Verifying…' : 'Verify Images'}
          </Button>
          <Button
            data-testid="seed-descriptions-btn"
            onClick={handleSeedDescriptions}
            disabled={seedingDesc}
            variant="outline"
            className="border-orange-600 text-orange-700 hover:bg-orange-50"
          >
            {seedingDesc ? <Loader2 className="animate-spin mr-2" size={16} /> : <FileText className="mr-2" size={16} />}
            {seedingDesc ? 'Updating…' : 'Refresh Descriptions'}
          </Button>
        </div>
      </div>

      {lastSync?.ran_at && (
        <div
          data-testid="last-sync-banner"
          className={`mb-6 rounded-xl p-3 text-sm flex items-center gap-2 ${
            lastSync.orphaned > 0
              ? 'bg-yellow-50 border border-yellow-200 text-yellow-800'
              : 'bg-green-50 border border-green-200 text-green-800'
          }`}
        >
          <RefreshCw size={16} />
          Image health: checked <strong>{lastSync.checked}</strong>, orphaned{' '}
          <strong>{lastSync.orphaned}</strong> • last verified{' '}
          {new Date(lastSync.ran_at).toLocaleString()}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
        </div>
      ) : (
        <>
          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-8">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Total Revenue</p>
                    <p className="text-2xl font-bold text-green-600">₹{stats?.total_revenue || 0}</p>
                  </div>
                  <IndianRupee className="text-green-600" size={32} />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Total Orders</p>
                    <p className="text-2xl font-bold text-blue-600">{stats?.total_orders || 0}</p>
                  </div>
                  <ShoppingCart className="text-blue-600" size={32} />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Products</p>
                    <p className="text-2xl font-bold text-purple-600">{stats?.total_products || 0}</p>
                  </div>
                  <Package className="text-purple-600" size={32} />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-500">Customers</p>
                    <p className="text-2xl font-bold text-orange-600">{stats?.total_customers || 0}</p>
                  </div>
                  <Users className="text-orange-600" size={32} />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Pending Orders Alert */}
          {stats?.pending_orders > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-8 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Clock className="text-yellow-600" size={24} />
                <div>
                  <p className="font-medium text-yellow-800">{stats.pending_orders} Pending Orders</p>
                  <p className="text-sm text-yellow-600">Require your attention</p>
                </div>
              </div>
              <Link to="/admin/orders?status=pending" className="text-yellow-700 hover:underline">
                View All →
              </Link>
            </div>
          )}

          {/* Recent Orders */}
          <Card>
            <CardHeader>
              <CardTitle>Recent Orders</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">Order</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">Customer</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">Total</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats?.recent_orders?.map((order) => (
                      <tr key={order.id} className="border-b">
                        <td className="px-4 py-3 font-mono text-sm">{order.order_number}</td>
                        <td className="px-4 py-3">{order.user_name}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {new Date(order.created_at).toLocaleDateString()}
                        </td>
                        <td className="px-4 py-3 font-medium">₹{order.total}</td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 text-xs rounded-full ${
                            order.order_status === 'delivered' ? 'bg-green-100 text-green-700' :
                            order.order_status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-blue-100 text-blue-700'
                          }`}>
                            {order.order_status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {(!stats?.recent_orders || stats.recent_orders.length === 0) && (
                  <p className="text-center py-8 text-gray-500">No orders yet</p>
                )}
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};
